import { useMemo, useState, useEffect, useRef } from "react";
import {
  Flex,
  View,
  Text,
  ActionButton,
  DialogTrigger,
  Dialog,
  Heading,
  ButtonGroup,
  Content,
  Button,
  Divider,
  TextField,
  StatusLight,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import styles from "./ChatHeader.module.css";
import { ChatTransferIcon, CloseIconBlack } from "../../common/Icons";
import Emitter from "../../../emitter";
import { API_CHAT_UPDATE } from "../../../constants/events";
import ChatTransferDialog from "../ChatTransferDialog/ChatTransferDialog";
import { RootState } from "../../../store/storeConfig";
import { getInitialsAndColor } from "../../../utils";
import { addTransferNoteAttribute } from "../../../api/session-api";
import { IndividualChat } from "../../../types/chat.type";
import Polling from "../../../utils/polling";

interface IChatHeaderProps {
  chatDetail: any;
  showChatDetail: boolean;
  handleShowChatDetail: () => void;
  activeChatId?: string;
}

const ChatHeader: React.FC<IChatHeaderProps> = (props) => {
  const { chatDetail, handleShowChatDetail, showChatDetail, activeChatId } =
    props;
  const common = useSelector((state: RootState) => state.common);
  const chats = useSelector((state: RootState) => state.chat);
  const { customer } = chatDetail || {};
  const [isTransferChatDialogOpen, setIsTransferChatDialogOpen] =
    useState<boolean>(false);
  const [isEndChatDialogOpen, setIsEndChatDialogOpen] = useState(false);
  const [reason, setReason] = useState("");

  const activeChat = chats.chats.find(
    (chatItem: IndividualChat) => chatItem.id === chatDetail?.id
  );

  const [chatDuration, setChatDuration] = useState<any>(null);
  const pollingCase: any = useRef(null);

  const getConnectedDuration = (chatDuration: number) => {
    let secs = Math.floor(chatDuration / 1000);
    let mins = 0;
    let hrs = 0;
    if (secs >= 60) {
      mins = Math.floor(secs / 60);
      secs = secs % 60;
    }
    if (mins >= 60) {
      hrs = Math.floor(mins / 60);
      mins = mins % 60;
    }

    const padNumber = (num: number) => num?.toString()?.padStart(2, "0");

    return `${padNumber(hrs)}:${padNumber(mins)}:${padNumber(secs)}`;
  };

  useEffect(() => {
    // Stop any existing timer
    if (pollingCase.current) {
      pollingCase.current.stop();
    }
    // Reset the duration state
    setChatDuration(null);

    // Start a new timer if the new active case is established
    if (activeChat?.self?.state === "ESTABLISHED") {
      pollingCase.current = new Polling(timer, 1000);
      startCaseTimer();
    }
    // Cleanup on unmount or when activeCase changes
    return () => {
      if (pollingCase.current) {
        pollingCase.current.stop();
      }
    };
  }, [activeChat?.id]);

  useEffect(() => {
    if (activeChat?.self?.state === "ESTABLISHED") {
      if (!pollingCase.current || !pollingCase.current.hasStarted()) {
        pollingCase.current = new Polling(timer, 1000);
        startCaseTimer();
      }
    }

    return () => {
      if (pollingCase.current) {
        pollingCase.current.stop();
      }
    };
  }, [activeChat?.self?.state]);

  const startCaseTimer = () => {
    if (!pollingCase.current.hasStarted()) {
      pollingCase.current.start();
    }
  };

  const timer = (continuePoll: any, pollingInstance: any) => {
    if (!activeChat || !activeChat.id) {
      pollingInstance.stop();
      setChatDuration(null);
      return;
    }

    const connectedTime =
      activeChat.self?.connectedTime || activeChat.customer?.connectedTime;
    const computedchatDuration = connectedTime
      ? new Date().getTime() - new Date(connectedTime).getTime() > 0
        ? new Date().getTime() - new Date(connectedTime).getTime()
        : 0
      : 0;
    setChatDuration(computedchatDuration);
    continuePoll();
  };

  const { initials, backgroundColor } = useMemo(() => {
    return getInitialsAndColor(customer?.name || "N A") as {
      initials: string;
      backgroundColor: string;
    };
  }, [customer?.name]);

  const chatTransferDialogHandler = () => {
    setIsTransferChatDialogOpen(true);
  };

  const rejectChatHandler = () => {
    addTransferNoteAttribute(chatDetail?.id, undefined, reason);
    Emitter.emit(API_CHAT_UPDATE, {
      body: {
        participantId: chatDetail?.id,
        conversationId: chatDetail?.id,
        state: "disconnected",
      },
    });
    setIsEndChatDialogOpen(false);
  };

  return (
    <Flex UNSAFE_className="chat-header" width={"100%"} height={"100%"}>
      <Flex
        direction="row"
        alignSelf="center"
        alignItems="center"
        UNSAFE_className={styles.chatheaderLeftSection}
      >
        <View margin="5px">
          <Flex
            width="40px"
            height="40px"
            justifyContent="center"
            alignItems="center"
            UNSAFE_className="chat-header-agent-avatar"
            UNSAFE_style={{
              backgroundColor,
              borderRadius: "50%",
            }}
          >
            <Text UNSAFE_className="chat-header-agent-text">{initials}</Text>
          </Flex>
        </View>

        <View margin="5px">
          <View UNSAFE_className="chat-agent-name">
            <Text UNSAFE_style={{ fontSize: "1rem" }}>
              {customer?.attributes?.FULLNAME}
            </Text>
          </View>
          <View>
            <Text UNSAFE_className="chat-agent-email">
              {customer?.attributes?.EMAIL}
            </Text>
          </View>
        </View>
      </Flex>

      <Flex
        direction={"row"}
        alignItems={"center"}
        justifyContent={"end"}
        height={"100%"}
        UNSAFE_className={styles.chatheaderRightSection}
      >
        <View>
          {isTransferChatDialogOpen && (
            <ChatTransferDialog
              isOpen={isTransferChatDialogOpen}
              onClose={() => setIsTransferChatDialogOpen(false)}
            />
          )}
        </View>
        {common.tab !== "History" && (
          <>
            {activeChat?.self?.state === "ESTABLISHED" && (
              <Flex UNSAFE_className={styles.chatInformationHeader}>
                <Text UNSAFE_className={styles.chatText}>Ongoing Chat</Text>
                <StatusLight
                  role="status"
                  variant="positive"
                  marginTop={"4px"}
                />
                <Text UNSAFE_className={styles.chatAuxTimer}>
                  {getConnectedDuration(chatDuration)}
                </Text>
              </Flex>
            )}
            <ActionButton
              onPress={chatTransferDialogHandler}
              UNSAFE_className={styles.chatInformationHeaderButton}
              isDisabled={!navigator.onLine}
            >
              <ChatTransferIcon />
              <p className={styles.caseInformationHeaderButtonText}>Transfer</p>
            </ActionButton>

            <DialogTrigger
              type="modal"
              isOpen={isEndChatDialogOpen}
              onOpenChange={setIsEndChatDialogOpen}
            >
              <ActionButton
                onPress={() => setIsEndChatDialogOpen(true)}
                UNSAFE_className={styles.chatInformationHeaderButton}
                isDisabled={!navigator.onLine}
              >
                <CloseIconBlack />
                <p className={styles.caseInformationHeaderButtonText}>
                  End chat
                </p>
              </ActionButton>

              {(close) => (
                <Dialog width={"440px"} maxWidth={"600px"}>
                  <Heading>End chat with {customer?.name} ?</Heading>
                  <Divider />
                  <Content>
                    <Text>
                      It's best to let the customer leave first. Do you still
                      want to end this chat?
                    </Text>
                    <TextField
                      label="Reason for ending chat"
                      value={reason}
                      onChange={setReason}
                      marginTop="size-200"
                      width={"100%"}
                    ></TextField>
                  </Content>
                  <ButtonGroup>
                    <Button variant="secondary" onPress={close}>
                      Cancel
                    </Button>
                    <Button variant="accent" onPress={rejectChatHandler}>
                      End Chat
                    </Button>
                  </ButtonGroup>
                </Dialog>
              )}
            </DialogTrigger>
            {/* <ActionButton
              aria-label="Icon only"
              marginEnd={10}
              onPress={handleShowChatDetail}
              UNSAFE_style={{ cursor: "pointer" }}
            >
              <span
                style={{
                  transform: showChatDetail ? "rotate(0deg)" : "rotate(180deg)",
                  transition: "transform 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ChevronDown />
              </span>
            </ActionButton> */}
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default ChatHeader;
